import { Pipe, PipeTransform } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { CommonService } from '../services/common.service';

@Pipe({
  name: 'getFormErrorMessage',
})
export class GetFormErrorMessage implements PipeTransform {
  private error = '';

  private warnings = {
    blank: this.cService.readyTranslations.form.required,
    invalidEmail: this.cService.readyTranslations.form.invalidEmail,
    mustMatch: this.cService.readyTranslations.form.mustMatch,
    invalid: this.cService.readyTranslations.form.invalid,
    number: this.cService.readyTranslations.form.number,
    uppercaseLetter: this.cService.readyTranslations.form.uppercaseLetter,
    lowercaseLetter: this.cService.readyTranslations.form.lowercaseLetter,
    specialCharacter: this.cService.readyTranslations.form.specialCharacter,
    minlength: this.cService.readyTranslations.form.minlength,
    maxlength: this.cService.readyTranslations.form.maxlength,
    characters: this.cService.readyTranslations.form.characters,
  };

  private validators = {
    required: 'required',
    email: 'email',
    mustMatch: 'mustMatch',
    invalid: 'pattern',
    number: 'number',
    uppercaseLetter: 'uppercaseLetter',
    lowercaseLetter: 'lowercaseLetter',
    specialCharacter: 'specialCharacter',
    minlength: 'minlength',
    maxlength: 'maxlength',
  };

  constructor(private cService: CommonService, private translate: TranslateService) {}

  transform(errorPrefix: string, controlLabel: string, formControl: AbstractControl, ...validations: any[]): string {
    this.error = '';
    errorPrefix = errorPrefix ? errorPrefix : '';

    for (let i = 0; i < validations.length; i++) {
      const errorCheck = Object.keys(validations[i])[i];

      if (this.shouldDisplayValidationError(errorCheck, formControl, 'required')) {
        this.setErrorMessage('blank', null, null, this.warnings.blank);
        break;
      }

      if (this.shouldDisplayValidationError(errorCheck, formControl, 'email')) {
        this.setErrorMessage('invalidEmail', null, null, this.warnings.invalidEmail);
        break;
      }

      if (this.shouldDisplayValidationError(errorCheck, formControl, 'mustMatch')) {
        this.setErrorMessage('mustMatch', controlLabel, null, `${controlLabel} ${this.warnings.mustMatch}`);
        break;
      }

      if (this.shouldDisplayValidationError(errorCheck, formControl, 'invalid')) {
        this.setErrorMessage('invalid', controlLabel, null, `${controlLabel} ${this.warnings.invalid}`);
        break;
      }

      if (this.shouldDisplayValidationError(errorCheck, formControl, 'number')) {
        this.setErrorMessage('number', controlLabel, null, `${controlLabel} ${this.warnings.number}`);
        break;
      }

      if (this.shouldDisplayValidationError(errorCheck, formControl, 'lowercaseLetter')) {
        this.setErrorMessage('lowercaseLetter', controlLabel, null, `${controlLabel} ${this.warnings.lowercaseLetter}`);
        break;
      }

      if (this.shouldDisplayValidationError(errorCheck, formControl, 'uppercaseLetter')) {
        this.setErrorMessage('uppercaseLetter', controlLabel, null, `${controlLabel} ${this.warnings.uppercaseLetter}`);
        break;
      }

      if (this.shouldDisplayValidationError(errorCheck, formControl, 'specialCharacter')) {
        this.setErrorMessage(
          'specialCharacter',
          controlLabel,
          null,
          `${controlLabel} ${this.warnings.specialCharacter}`
        );
        break;
      }

      if (this.shouldDisplayValidationError(errorCheck, formControl, 'minlength')) {
        this.setErrorMessage(
          'minlength',
          controlLabel,
          { value: validations[i].minlength.requiredLength },
          `${controlLabel} ${this.warnings.minlength} ${validations[i].minlength.requiredLength} ${this.warnings.characters}`
        );
        break;
      }

      if (this.shouldDisplayValidationError(errorCheck, formControl, 'maxlength')) {
        this.setErrorMessage(
          'maxlength',
          controlLabel,
          { value: validations[i].maxlength.requiredLength },
          `${controlLabel} ${this.warnings.maxlength} ${validations[i].maxlength.requiredLength} ${this.warnings.characters}`
        );
        break;
      }
    }

    this.error = this.error ? `${errorPrefix} ${this.error}` : '';

    return this.error;
  }

  shouldDisplayValidationError(errorCheck: string, formControl: AbstractControl, validator: string) {
    return (
      errorCheck.toLowerCase() === this.validators[validator].toLowerCase() &&
      formControl.hasError(this.validators[validator])
    );
  }

  setErrorMessage(
    validator: string,
    label: string | null,
    values: { [key: string]: string } | null = {},
    fallback: string
  ): void {
    let labelKey;

    if (label) {
      const keyEnd = validator.charAt(0).toUpperCase() + validator.slice(1);
      const keyStart = label.charAt(0).toLowerCase() + label.slice(1);
      labelKey = keyStart + keyEnd;
    }

    this.translate.get(`FORM.${labelKey}`, values).subscribe({
      next: (translation) => {
        if (translation !== `FORM.${labelKey}`) {
          this.error = translation;
        } else {
          this.error = fallback;
        }
      },
    });
  }
}
